package com.arena_web.data.api

import com.arena_web.data.dto.*
import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.apiDelete
import com.arena_web.data.http.apiGet
import com.arena_web.data.http.apiPost
import com.arena_web.data.http.apiPostMultipart
import com.arena_web.data.http.apiPut
import io.ktor.client.*

class ChatApi(private val client: HttpClient) {

    // ── Chat CRUD ──
    suspend fun createDirectChat(request: CreateDirectChatRequest): ApiResponse<ChatDto> {
        return client.apiPost("v1/chats/direct", request)
    }

    suspend fun createGroupChat(request: CreateGroupChatRequest): ApiResponse<ChatDto> {
        return client.apiPost("v1/chats/group", request)
    }

    suspend fun getChats(params: Map<String, String> = emptyMap()): ApiResponse<ChatListPageDto> {
        return client.apiGet("v1/chats", params)
    }

    suspend fun getChat(chatId: String): ApiResponse<ChatDto> {
        return client.apiGet("v1/chats/$chatId")
    }

    suspend fun updateGroupChat(chatId: String, request: UpdateGroupChatRequest): ApiResponse<ChatDto> {
        return client.apiPut("v1/chats/$chatId", request)
    }

    suspend fun uploadChatImage(
        chatId: String,
        fileBytes: ByteArray,
        fileName: String,
        contentType: String
    ): ApiResponse<ImageUploadResponse> {
        return client.apiPostMultipart("v1/chats/$chatId/image", fileBytes, fileName, contentType)
    }

    suspend fun leaveChat(chatId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/chats/$chatId")
    }

    // ── Messages ──
    suspend fun getMessages(chatId: String, params: Map<String, String> = emptyMap()): ApiResponse<MessagesPageDto> {
        return client.apiGet("v1/chats/$chatId/messages", params)
    }

    suspend fun sendMessage(chatId: String, request: SendMessageRequest): ApiResponse<MessageDto> {
        return client.apiPost("v1/chats/$chatId/messages", request)
    }

    suspend fun markAsRead(chatId: String): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/read")
    }

    suspend fun deleteMessage(chatId: String, messageId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/chats/$chatId/messages/$messageId")
    }

    suspend fun editMessage(chatId: String, messageId: String, request: EditMessageRequest): ApiResponse<MessageDto> {
        return client.apiPut("v1/chats/$chatId/messages/$messageId", request)
    }

    suspend fun sendMediaMessage(
        chatId: String,
        fileBytes: ByteArray,
        fileName: String,
        fileContentType: String,
        duration: Int? = null
    ): ApiResponse<MessageDto> {
        val fields = mutableMapOf<String, String>()
        if (duration != null) fields["duration"] = duration.toString()
        return client.apiPostMultipart(
            "v1/chats/$chatId/messages/media",
            fileBytes, fileName, fileContentType, fields
        )
    }

    // ── Reactions ──
    suspend fun addReaction(chatId: String, messageId: String, request: AddReactionRequest): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/messages/$messageId/reactions", request)
    }

    suspend fun removeReaction(chatId: String, messageId: String, emoji: String): ApiResponse<Unit> {
        return client.apiDelete("v1/chats/$chatId/messages/$messageId/reactions/$emoji")
    }

    // ── Pin ──
    suspend fun togglePin(chatId: String, messageId: String): ApiResponse<MessageDto> {
        return client.apiPost("v1/chats/$chatId/messages/$messageId/pin")
    }

    suspend fun getPinnedMessages(chatId: String): ApiResponse<List<MessageDto>> {
        return client.apiGet("v1/chats/$chatId/messages/pinned")
    }

    // ── Forward ──
    suspend fun forwardMessage(
        chatId: String,
        messageId: String,
        request: ForwardMessageRequest
    ): ApiResponse<MessageDto> {
        return client.apiPost("v1/chats/$chatId/messages/$messageId/forward", request)
    }

    // ── Search ──
    suspend fun searchMessages(chatId: String, params: Map<String, String>): ApiResponse<MessagesPageDto> {
        return client.apiGet("v1/chats/$chatId/messages/search", params)
    }

    // ── Members ──
    suspend fun kickMember(chatId: String, userId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/chats/$chatId/members/$userId")
    }

    // ── Invitations ──
    suspend fun inviteToChat(chatId: String, request: InviteToChatRequest): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/invite", request)
    }

    suspend fun getInvitations(): ApiResponse<List<ChatInvitationDto>> {
        return client.apiGet("v1/chats/invitations")
    }

    suspend fun respondToInvitation(invitationId: String, request: RespondToInvitationRequest): ApiResponse<Unit> {
        return client.apiPost("v1/chats/invitations/$invitationId/respond", request)
    }

    // ── Join requests ──
    suspend fun joinChat(chatId: String): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/join")
    }

    suspend fun getJoinRequests(chatId: String): ApiResponse<List<ChatJoinRequestDto>> {
        return client.apiGet("v1/chats/$chatId/join-requests")
    }

    suspend fun respondToJoinRequest(
        chatId: String,
        requestId: String,
        request: RespondToJoinRequestRequest
    ): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/join-requests/$requestId/respond", request)
    }

    // ── Mute & Online ──
    suspend fun muteChat(chatId: String, request: MuteChatRequest): ApiResponse<Unit> {
        return client.apiPost("v1/chats/$chatId/mute", request)
    }

    suspend fun getOnlineUsers(chatId: String): ApiResponse<OnlineUsersResponse> {
        return client.apiGet("v1/chats/$chatId/online")
    }
}
